import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { FuaFormField } from 'components/Assessments/FuaAssessment/types/FuaFormField'
import { MedicalBackgroundQuestionnaireItem } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/questionnaire'
import { PainFormField } from './formField'

/**
 * Pain subsection adapter
 * Handles pain assessment including FLACC scale components (8 fields)
 */
export class PainAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Pain assessment fields
    const painStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_STATUS
    )?.at(0)

    const painGoal = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_GOAL
    )?.at(0)

    // FLACC Scale components (Face, Legs, Activity, Cry, Consolability)
    const flaccScaleFace = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_FACE
    )?.at(0)

    const flaccScaleLegs = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_LEGS
    )?.at(0)

    const flaccScaleActivity = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_ACTIVITY
    )?.at(0)

    const flaccScaleCry = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CRY
    )?.at(0)

    const flaccScaleConsolability = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CONSOLABILITY
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [PainFormField.PAIN_STATUS]: painStatus,
      [PainFormField.PAIN_GOAL]: painGoal,
      [PainFormField.PAIN_FLACC_SCALE_FACE]: flaccScaleFace,
      [PainFormField.PAIN_FLACC_SCALE_LEGS]: flaccScaleLegs,
      [PainFormField.PAIN_FLACC_SCALE_ACTIVITY]: flaccScaleActivity,
      [PainFormField.PAIN_FLACC_SCALE_CRY]: flaccScaleCry,
      [PainFormField.PAIN_FLACC_SCALE_CONSOLABILITY]: flaccScaleConsolability,
      [PainFormField.PAIN_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_STATUS,
        value: this.getFormDataField(FuaFormField.PAIN_STATUS)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_FACE,
        value: this.getFormDataField(FuaFormField.PAIN_FLACC_SCALE_FACE)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_LEGS,
        value: this.getFormDataField(FuaFormField.PAIN_FLACC_SCALE_LEGS)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_ACTIVITY,
        value: this.getFormDataField(FuaFormField.PAIN_FLACC_SCALE_ACTIVITY)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CRY,
        value: this.getFormDataField(FuaFormField.PAIN_FLACC_SCALE_CRY)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_FLACC_SCALE_CONSOLABILITY,
        value: this.getFormDataField(FuaFormField.PAIN_FLACC_SCALE_CONSOLABILITY)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_SYSTEM_SUMMARY,
        value: this.getFormDataField(FuaFormField.PAIN_SYSTEM_SUMMARY)
      }
    ]

    // Conditionally add pain goal if it exists (it is conditionally rendered when patient has pain)
    if (this.formDataFieldExists(PainFormField.PAIN_GOAL)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_PAIN_GOAL,
        value: this.getFormDataField(PainFormField.PAIN_GOAL)
      })
    }

    return mutations
  }
}
